<template>
  <WdsModal
    :show="show"
    :title="$t('Your profile for :company', { company: companyName })"
    :z-index="1000"
    class="recruiter-profile-modal"
    disable-click-outside
    @cancel="$emit('close')"
  >
    <template #default>
      <WdsStack direction-column gap-md>
        <WdsText secondary>
          {{
            $t(
              'Manage your staffing agency profile for :company. All data provided is shared with the client.',
              { company: companyName },
            )
          }}
        </WdsText>

        <WdsCollapse v-if="hasDocuments" :open="true" content-id="required-documents-recruiter">
          <template #title>
            <WdsStack justify-space-between align-center class="tw-w-full tw-py-2">
              <WdsStack align-center gap-sm>
                <WdsText large class="tw-font-semibold">
                  {{ $t('Required documents') }}
                  <span class="tw-text-indigo-500 tw-font-bold">*</span>
                </WdsText>
              </WdsStack>
              <WdsLabel
                :success="documentsCompleted"
                :warning="!documentsCompleted"
                :subtle="documentsCompleted"
                sm
              >
                {{ documentsCompleted ? $t('Completed') : $t('Pending') }}
              </WdsLabel>
            </WdsStack>
          </template>
          <template #content>
            <WdsStack v-if="documentsLoading" justify-center align-center class="tw-py-4">
              <WdsSpinner indeterminate />
            </WdsStack>
            <WdsStack v-else direction-column>
              <WdsText secondary sm>
                {{ $t('Acceptance required by :company', { company: companyName }) }}
              </WdsText>

              <WdsStack direction-column gap-md class="tw-mt-4">
                <WdsStack
                  v-for="document in documents"
                  :key="document.id"
                  class="tw-border tw-border-gray-200 tw-rounded-md tw-p-4 tw-bg-white"
                  justify-space-between
                  align-center
                  gap-sm
                >
                  <WdsStack align-center gap-sm>
                    <WdsIcon :name="WdsIconNameEnum.DOCUMENT" :size="20" />
                    <a
                      v-if="document.isAccepted && document.pdfUrl"
                      :href="document.pdfUrl"
                      target="_blank"
                      class="tw-text-indigo-500 hover:tw-underline"
                    >
                      {{ document.file?.title ?? document.file?.name ?? $t('Document') }}
                    </a>
                    <WdsText v-else>
                      {{ document.file?.title ?? document.file?.name ?? $t('Document') }}
                    </WdsText>
                  </WdsStack>
                  <WdsStack gap-sm align-center>
                    <WdsText v-if="document.isAccepted" sm>
                      <span class="tw-text-green-500">
                        {{ $t('Accepted') }}
                      </span>
                    </WdsText>
                    <WdsButton
                      v-if="!document.isAccepted"
                      secondary
                      sm
                      @click="openAcceptDocModal(document)"
                    >
                      {{ $t('Review & accept') }}
                    </WdsButton>
                  </WdsStack>
                </WdsStack>
              </WdsStack>
            </WdsStack>
          </template>
        </WdsCollapse>

        <AdditionalInformationSection
          :company-name="companyName"
          :fields="customFields.fields"
          :fields-loading="customFields.fieldsLoading"
          :model-value="customFields.localFieldValues"
          :field-value-fragments="customFields.initialFieldValueFragments"
          :errors="customFields.fieldErrors"
          :completed="customFields.customFieldsCompleted"
          :show="customFields.showCustomFieldsSection"
          @update:model-value="updateLocalFieldValues"
        />
      </WdsStack>
    </template>

    <template #footer>
      <WdsStack justify-end class="tw-w-full">
        <WdsButton
          v-if="customFields.hasCustomFields"
          primary
          :loading="customFields.saving"
          @click="handleSave"
        >
          {{ $t('Save') }}
        </WdsButton>
      </WdsStack>
    </template>
  </WdsModal>

  <AcceptContractDocModal
    v-if="selectedDocument"
    v-model="showAcceptDocModal"
    :contract-doc="selectedDocument"
    @update:model-value="handleDocModalClose"
    @accepted="handleDocAccepted"
  />
</template>

<script setup lang="ts">
import { computed, reactive, ref, toRef } from 'vue'
import {
  WdsButton,
  WdsCollapse,
  WdsIcon,
  WdsIconNameEnum,
  WdsLabel,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { useTranslation } from '@/composables/translation'
import AcceptContractDocModal from '@/components/compliances/modals/AcceptContractDocModal.vue'
import AdditionalInformationSection from '@/components/Fieldable/AdditionalInformationSection.vue'
import useProfileModalCustomFields from '@/components/Fieldable/composables/useProfileModalCustomFields'
import { FieldableFormCustomFieldValueFragment } from '@/components/Fieldable/graphql/fragments'
import type { FragmentType } from '@/gql/fragment-masking'
import { TypeSupportingCustomFieldValues } from '@/gql/graphql'
import type { CustomFieldTypeValueInput } from '@/gql/graphql'

const { $t } = useTranslation()

interface OnboardingDoc {
  id: string
  pdfUrl: string | null
  isAccepted: boolean
  file: {
    id: string
    name: string
    title: string | null
  } | null
}

const props = defineProps<{
  show: boolean
  clientId: string
  companyName: string
  companyId: string
  documents: OnboardingDoc[]
  documentsLoading?: boolean
  customFieldValues: FragmentType<typeof FieldableFormCustomFieldValueFragment>[]
}>()

const emit = defineEmits<{
  close: []
  saved: []
  'document-accepted': []
}>()

const showAcceptDocModal = ref(false)
const selectedDocument = ref<{ localId: number; pdfUrl: string } | null>(null)

const hasDocuments = computed(() => props.documents.length > 0)
const documentsLoading = computed(() => props.documentsLoading ?? false)
const documentsCompleted = computed(() => props.documents.every((doc) => doc.isAccepted))

const customFields = reactive(
  useProfileModalCustomFields(
    props.companyId,
    props.clientId,
    TypeSupportingCustomFieldValues.CompanyRecruiter,
    toRef(props, 'show'),
    computed(() => props.customFieldValues),
  ),
)

const updateLocalFieldValues = (value: CustomFieldTypeValueInput[]) => {
  customFields.localFieldValues = value
}

const handleSave = async () => {
  const ok = await customFields.handleSave()
  if (ok) emit('saved')
}

const openAcceptDocModal = (document: OnboardingDoc) => {
  if (!document.pdfUrl) return
  const localId = parseInt(atob(document.id).split(':')[1], 10)
  selectedDocument.value = { localId, pdfUrl: document.pdfUrl }
  showAcceptDocModal.value = true
}

const handleDocModalClose = () => {
  selectedDocument.value = null
  showAcceptDocModal.value = false
}

const handleDocAccepted = () => {
  handleDocModalClose()
  emit('document-accepted')
}
</script>

<style scoped>
.recruiter-profile-modal :deep(.wds-modal-content) {
  width: 90%;
  max-width: 600px;
}

.recruiter-profile-modal :deep(.wds-modal-body) {
  margin-top: 0;
}

@media (min-width: 768px) {
  .recruiter-profile-modal :deep(.wds-modal-content) {
    width: 60%;
  }
}
</style>
